/* eslint-disable max-lines-per-function */
import { ReactNode, useMemo, useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { CalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { useParams } from 'react-router-dom';
import { YearMonthParams } from '@/domain/termine/type';
import dayjs from 'dayjs';
import { useTagesDaten } from '@/domain/termine/hooks/useTagesDaten';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { generateWiederholungen } from '@/domain/termine/shared/utils/generateWiederholungen';
import { Termin } from '@/domain/termine/shared/types/Termin';
import { useTerminKalender } from '@/domain/termine/hooks/useTerminKalender';

interface CalendarProviderProps {
  children: ReactNode;
}

function useCalendarDateRange() {
  const { year, month } = useParams<YearMonthParams>();

  const monthDate = useMemo(
    () => ({
      year: parseInt(year ?? `${new Date().getFullYear()}`),
      month: parseInt(month ?? `${new Date().getMonth() + 1}`),
    }),
    [year, month],
  );

  const firstDateOfDisplayedMonth = dayjs(
    `${monthDate.year}-${monthDate.month}`,
  );

  const startDate = firstDateOfDisplayedMonth.startOf('month').startOf('week');
  const endDate = firstDateOfDisplayedMonth.endOf('month').endOf('week');

  return { monthDate, startDate, endDate };
}

function useTermineWithRecurrences(
  termine: Termin[],
  standortId: string | undefined,
  selectedKalenderIds: string[],
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
) {
  return useMemo(() => {
    const standortTermine = standortId
      ? termine.filter((t) => t.standortId === standortId)
      : termine;
    const filteredTermineByStandortAndKalender =
      selectedKalenderIds.length > 0
        ? standortTermine.filter((t) =>
            t.idTerminkalender
              ? selectedKalenderIds.includes(t.idTerminkalender)
              : selectedKalenderIds.includes('NO_KALENDER'),
          )
        : [];
    return filteredTermineByStandortAndKalender.flatMap((termin) =>
      generateWiederholungen(termin, startDate, endDate),
    );
  }, [termine, standortId, selectedKalenderIds, startDate, endDate]);
}

export function CalendarProvider({ children }: CalendarProviderProps) {
  const { currentStandort } = useStandortContext();
  const { monthDate, startDate, endDate } = useCalendarDateRange();

  const standortKalenderKey = `deselectedKalenderIds_${currentStandort.id || 'none'}`;
  const [deselectedKalenderIds, setDeselectedKalenderIds] =
    useLocalStorageState<string[]>(standortKalenderKey, { defaultValue: [] });

  const { termine, feiertage, loading } = useTagesDaten([currentStandort.id], {
    von: startDate.format('YYYY-MM-DD'),
    bis: endDate.format('YYYY-MM-DD'),
  });

  const terminKalender = useTerminKalender(
    currentStandort.id ? [currentStandort.id] : [],
    {
      von: startDate.format('YYYY-MM-DD'),
      bis: endDate.format('YYYY-MM-DD'),
    },
  );
  const kalender = terminKalender.kalender;

  const kalenderIds = useMemo(() => kalender.map((k) => k.id), [kalender]);

  const selectedKalenderIds = useMemo(
    () => kalenderIds.filter((id) => !deselectedKalenderIds.includes(id)),
    [kalenderIds, deselectedKalenderIds],
  );

  const setSelectedKalenderIds = useCallback(
    (ids: string[]) => {
      setDeselectedKalenderIds(kalenderIds.filter((id) => !ids.includes(id)));
    },
    [kalenderIds, setDeselectedKalenderIds],
  );

  const termineWithRecurrences = useTermineWithRecurrences(
    termine,
    currentStandort.id,
    selectedKalenderIds,
    startDate,
    endDate,
  );

  const termineByDay = useMemo(() => {
    const map = new Map<string, Termin[]>();
    termineWithRecurrences.forEach((termin) => {
      const key = dayjs(termin.datum).format('YYYY-MM-DD');
      const existing = map.get(key) ?? [];
      map.set(key, [...existing, termin]);
    });
    return map;
  }, [termineWithRecurrences]);

  const value = useMemo(
    () => ({
      loading,
      feiertage,
      kalender,
      standortId: currentStandort.id,
      startDate,
      endDate,
      monthDate,
      termineWithRecurrences,
      termineByDay,
      selectedKalenderIds,
      setSelectedKalenderIds,
    }),
    [
      loading,
      feiertage,
      kalender,
      currentStandort.id,
      startDate,
      endDate,
      monthDate,
      termineWithRecurrences,
      termineByDay,
      setSelectedKalenderIds,
      selectedKalenderIds,
    ],
  );

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}
